import React, { useEffect, useState } from "react"
import { useFormik } from "formik"
import {
	Box,
	FormControl,
	TextField,
	makeStyles,
	Button,
	Typography,
	Select,
	MenuItem,
	FormHelperText,
	InputAdornment,
	IconButton,
	Tooltip,
	InputLabel,
} from "@material-ui/core"
import * as yup from "yup"
import { useTranslation } from "react-i18next"
import { KeyboardArrowDown } from "@material-ui/icons"
import VisibilityOffSVG from "../icons/VisibilityOffSVG"
import VisibilitySVG from "../icons/VisibilitySVG"
import InfoIconSVG from "../icons/InfoIconSVG"

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	margin: {
		marginBottom: theme.spacing(3),
		"& .Mui-error": {
			marginLeft: 0,
			marginRight: 0,
		},
		"& .MuiFormLabel-root": {
			fontSize: theme.typography.fontSize,
		},
		"& .MuiFormHelperText-contained": {
			marginLeft: 0,
			marginRight: 0,
			color: theme.palette.error.main,
		},
	},
	dividerLine: {
		width: "100%",
		border: `1px solid ${theme.palette.grey[400]}`,
		margin: theme.spacing(2),
	},
	formActions: {
		display: "flex",
		justifyContent: "flex-end",
		flexDirection: "row",
		alignItems: "center",
	},
	formActionsBtn: {
		...theme.typography.fieldValue,
		fontWeight: 500,
		margin: "0.5rem",
		padding: "4px 12px",
		textTransform: "capitalize",
	},
	required: {
		color: theme.palette.error.main,
	},
	textLabel: {
		...theme.typography.fieldValue,
		paddingBottom: 10,
	},
	textField: {
		"& .MuiOutlinedInput-input": {
			//padding: "12px 13px",
		},
		"& fieldset > legend": {
			float: "none",
		},
	},
	userRoleDropdown: {
		display: "flex",
		flexDirection: "row",
		alignItems: "flex-end",
		padding: "2px",
		gap: "2px",
		borderRadius: "3px",
		fontSize: theme.typography.fontSize,
		"& .MuiSelect-icon": {
			color: theme.palette.grey["D300"],
		},
		"& .MuiInput-input": {
			padding: "12px 13px",
		},
		"& fieldset > legend": {
			float: "none",
		},

		//border: `1px solid ${theme.palette.grey[400]}`,
	},
	menuItemStyle: {
		marginTop: theme.spacing(1),
	},
}))

function EditUser(props) {
	const classes = useStyles()
	const [t] = useTranslation("global")
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)

	// TODO: Need to remove the hard coded data while implementing the APIs
	const userRoles = ["Case Admin", "Case User", "System Admin"]
	const caseList = ["FOIA", "Emails With Metadata", "Demo Case"]

	const userValidationSchema = yup.object({
		firstName: yup.string().required(t("user.formErrors.required")),
		lastName: yup.string().required(t("user.formErrors.required")),
		userRoleSelect: yup.array().of(yup.string().required(t("user.formErrors.required"))),
		casesSelect: yup.array().of(yup.string().required(t("user.formErrors.required"))),
		email: yup.string().email(t("user.formErrors.email")).required(t("user.formErrors.required")),
		password: yup
			.string()
			.required(t("user.formErrors.required"))
			.matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[(!@#$%^&*.)])[A-Za-z\d(!@#$%^&*.)]{8,20}$/, t("user.formErrors.password")),
		confirmPassword: yup
			.string()
			.required(t("user.formErrors.required"))
			.oneOf([yup.ref("password"), null], t("user.formErrors.passwordMustMatch")),
	})

	const handleFormSubmit = (formData) => {
		console.log("Form Data", formData)
	}

	const handleFieldChange = (values) => {
		console.log("Changed values", values)
	}

	const addUserForm = useFormik({
		validateOnMount: true,
		initialValues: {
			firstName: "",
			lastName: "",
			email: "",
			password: "",
			confirmPassword: "",
			userRoleSelect: [],
			casesSelect: [],
		},
		validationSchema: userValidationSchema,
		handleChange: handleFieldChange,
		onSubmit: handleFormSubmit,
	})

	return (
		<form className={classes.root} noValidate autoComplete="off" id="create-user" onSubmit={addUserForm.handleSubmit}>
			<Box>
				<FormControl fullWidth className={classes.margin}>
					<TextField
						id="firstName"
						label={`${t("user.formFields.firstName")}*`}
						variant="outlined"
						placeholder={t("user.formFields.firstName")}
						className={classes.textField}
						value={addUserForm.values.firstName}
						onChange={addUserForm.handleChange}
						error={addUserForm.touched.firstName && Boolean(addUserForm.errors.firstName)}
						helperText={addUserForm.touched.firstName && addUserForm.errors.firstName}
					/>
				</FormControl>
				<FormControl fullWidth className={classes.margin}>
					<TextField
						id="lastName"
						variant="outlined"
						label={`${t("user.formFields.lastName")}*`}
						placeholder={t("user.formFields.lastName")}
						className={classes.textField}
						value={addUserForm.values.lastName}
						onChange={addUserForm.handleChange}
						error={addUserForm.touched.lastName && Boolean(addUserForm.errors.lastName)}
						helperText={addUserForm.touched.lastName && addUserForm.errors.lastName}
					/>
				</FormControl>
				<FormControl fullWidth className={classes.margin}>
					<TextField
						id="email"
						variant="outlined"
						label={`${t("user.formFields.email")}*`}
						placeholder={t("user.formFields.email")}
						className={classes.textField}
						value={addUserForm.values.email}
						onChange={addUserForm.handleChange}
						error={addUserForm.touched.email && Boolean(addUserForm.errors.email)}
						helperText={addUserForm.touched.email && addUserForm.errors.email}
						disabled
					/>
				</FormControl>
				<FormControl fullWidth className={classes.margin}>
					{/* Keeping this for refrence */}
					{/* <Typography className={classes.textLabel}>
						{t("user.formFields.password")}
						<Box component="span" className={classes.required}>
							*
						</Box>
						<Tooltip title={t("user.formErrors.password")} placement="top">
							<IconButton>
								<InfoIconSVG />
							</IconButton>
						</Tooltip>
					</Typography> */}
					<TextField
						id="password"
						type={showPassword ? "text" : "password"}
						variant="outlined"
						label={`${t("user.formFields.password")}*`}
						placeholder={t("user.formFields.password")}
						className={classes.textField}
						value={addUserForm.values.password}
						onChange={addUserForm.handleChange}
						error={addUserForm.touched.password && Boolean(addUserForm.errors.password)}
						helperText={addUserForm.touched.password && addUserForm.errors.password}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)}>
										{showPassword ? <VisibilitySVG /> : <VisibilityOffSVG />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				</FormControl>
				<FormControl fullWidth className={classes.margin}>
					<TextField
						id="confirmPassword"
						type={showConfirmPassword ? "text" : "password"}
						label={`${t("user.formFields.confirmPassword")}*`}
						variant="outlined"
						placeholder={t("user.formFields.confirmPassword")}
						className={classes.textField}
						value={addUserForm.values.confirmPassword}
						onChange={addUserForm.handleChange}
						error={addUserForm.touched.confirmPassword && Boolean(addUserForm.errors.confirmPassword)}
						helperText={addUserForm.touched.confirmPassword && addUserForm.errors.confirmPassword}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										onClick={() => setShowConfirmPassword(!showConfirmPassword)}
									>
										{showConfirmPassword ? <VisibilitySVG /> : <VisibilityOffSVG />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				</FormControl>
				<FormControl fullWidth variant="outlined" className={classes.margin}>
					<InputLabel id="userRoleSelectLabel">{t("user.formFields.assignUserRole.label")}*</InputLabel>
					<Select
						id="userRoleSelect"
						name="userRoleSelect"
						labelId="userRoleSelectLabel"
						label={`${t("user.formFields.assignUserRole.label")}*`}
						variant="outlined"
						value={addUserForm.values.userRoleSelect}
						onChange={(selectedOption) => {
							addUserForm.handleChange(selectedOption)
						}}
						displayEmpty
						className={classes.userRoleDropdown}
						disableUnderline
						MenuProps={{
							anchorOrigin: {
								vertical: "bottom",
								horizontal: "left",
							},
							getContentAnchorEl: null,
							PaperProps: {
								style: {
									width: "250px",
									overflowX: "scroll",
								},
							},
						}}
						multiple={true}
						renderValue={(selected) => {
							// if (selected.length === 0) {
							// 	return <em>{t("user.formFields.assignUserRole.select")}</em>
							// }

							return selected.join(", ")
						}}
						IconComponent={KeyboardArrowDown}
					>
						<MenuItem value="" disabled className={classes.menuItemStyle}>
							<em>{t("user.formFields.assignUserRole.select")}</em>
						</MenuItem>
						{userRoles.map((role) => (
							<MenuItem key={role} value={role} className={classes.menuItemStyle}>
								{role}
							</MenuItem>
						))}
					</Select>
					{addUserForm.touched.userRoleSelect && !addUserForm.values.userRoleSelect?.length && (
						<FormHelperText style={{ marginTop: "2px" }}>{t("user.formErrors.required")}</FormHelperText>
					)}
				</FormControl>
				<FormControl variant="outlined" fullWidth className={classes.margin}>
					<InputLabel id="casesSelectLabel">{t("user.formFields.addCases.label")}*</InputLabel>
					<Select
						id="casesSelect"
						name="casesSelect"
						labelId="casesSelectLabel"
						label={t("user.formFields.addCases.label")}
						variant="outlined"
						value={addUserForm.values.casesSelect}
						onChange={(selectedOption) => {
							addUserForm.handleChange(selectedOption)
						}}
						displayEmpty
						className={classes.userRoleDropdown}
						disableUnderline
						MenuProps={{
							anchorOrigin: {
								vertical: "bottom",
								horizontal: "left",
							},
							getContentAnchorEl: null,
							PaperProps: {
								style: {
									width: "250px",
									overflowX: "scroll",
								},
							},
						}}
						multiple={true}
						renderValue={(selected) => {
							return selected.join(", ")
						}}
						IconComponent={KeyboardArrowDown}
					>
						<MenuItem value="" disabled className={classes.menuItemStyle}>
							{t("user.formFields.addCases.select")}
						</MenuItem>
						{caseList.map((caseItem) => (
							<MenuItem key={caseItem} value={caseItem} className={classes.menuItemStyle}>
								{caseItem}
							</MenuItem>
						))}
					</Select>
					{addUserForm.touched.casesSelect && !addUserForm.values.casesSelect?.length && (
						<FormHelperText style={{ margin: "2px" }}>{t("user.formErrors.required")}</FormHelperText>
					)}
				</FormControl>
				<hr className={classes.dividerLine} />
				<FormControl className={classes.formActions}>
					<Button variant="contained" size="small" className={classes.formActionsBtn} onClick={() => addUserForm?.resetForm()}>
						{t("user.formActions.cancel")}
					</Button>
					<Button
						variant="contained"
						size="small"
						className={classes.formActionsBtn}
						type="submit"
						disabled={!addUserForm.isValid}
					>
						{t("user.formActions.update")}
					</Button>
				</FormControl>
			</Box>
		</form>
	)
}

export default EditUser
